// src/utils/analytics.js

const ANALYTICS_DISABLED_KEY = "bbx:analytics-disabled";
const COOKIE_CONSENT_KEY = "buukkeri.cookieConsent.v1";

/**
 * Onko käyttäjä hyväksynyt analytiikan?
 */
function hasAnalyticsConsent() {
  if (typeof window === "undefined") return false;

  try {
    const saved = window.localStorage.getItem(COOKIE_CONSENT_KEY);

    if (!saved) {
      return false;
    }

    const consent = JSON.parse(saved);

    return consent?.analytics === true;
  } catch {
    return false;
  }
}

/**
 * Gtag olemassa?
 */
function hasGtag() {
  return typeof window !== "undefined" && typeof window.gtag === "function";
}

/**
 * Lukee URL-parametrit ja tallentaa laite-/selainkohtaisen GA4-eston.
 *
 * Käyttö:
 * - /?ga_off=1  -> estää GA4-eventit tässä selaimessa
 * - /?ga_on=1   -> palauttaa GA4-eventit tässä selaimessa
 *
 * Huom:
 * Tämä ei tunnista fyysistä laitetta, vaan selaimen localStoragen.
 * Siksi esto pitää asettaa erikseen läppärin selaimeen ja puhelimen selaimeen.
 */
function syncAnalyticsFlagFromUrl() {
  if (typeof window === "undefined") return;

  try {
    const qs = new URLSearchParams(window.location.search || "");

    if (qs.get("ga_off") === "1") {
      window.localStorage.setItem(ANALYTICS_DISABLED_KEY, "1");
    }

    if (qs.get("ga_on") === "1") {
      window.localStorage.removeItem(ANALYTICS_DISABLED_KEY);
    }
  } catch {
    // Ei kaadeta sovellusta, jos localStorage ei ole käytettävissä.
  }
}

/**
 * Tarkistaa, pitääkö GA4-eventit jättää lähettämättä.
 */
function shouldSkipAnalytics(eventName = "") {
  if (typeof window === "undefined") return true;

  syncAnalyticsFlagFromUrl();

  /**
   * Varsinaisia GA4-eventtejä ei lähetetä ennen kuin
   * käyttäjä on hyväksynyt analytiikan.
   */
  if (!hasAnalyticsConsent()) {
    return true;
  }

  try {
    if (window.localStorage.getItem(ANALYTICS_DISABLED_KEY) === "1") {
      return true;
    }
  } catch {
    // Jos localStorage ei toimi, jatketaan muihin tarkistuksiin.
  }

  const host = window.location.hostname || "";
  const path = window.location.pathname || "";

  /**
   * Ei kerätä paikallista tai staging-liikennettä.
   */
  if (
    host === "localhost" ||
    host === "127.0.0.1" ||
    host === "0.0.0.0" ||
    host.includes("staging.buukkeri.fi") ||
    host.includes("buukkeri-staging.fi")
  ) {
    return true;
  }

   /**
   * Ei kerätä hallinta-, kirjautumis- tai setup-näkymiä GA4:ään.
   * Näin hallintapaneelin oma käyttö ei sotke julkisen sivuston analytiikkaa.
   *
   * Rekisteröitymissivu pysyy muuten analytiikan ulkopuolella,
   * mutta onnistunut tilin luonti saadaan mitata GA4:n sign_up-eventtinä.
   */
  const isSignupEvent = eventName === "sign_up";

  if (
    path.startsWith("/dashboard") ||
    path.startsWith("/kirjaudu") ||
    path.startsWith("/login") ||
    (path.startsWith("/rekisteroidy") && !isSignupEvent) ||
    path.startsWith("/onboarding") ||
    path.startsWith("/setup") ||
    path.startsWith("/forgot") ||
    path.startsWith("/reset") ||
    path.startsWith("/verify-email") ||
    path.startsWith("/oauth/")
  ) {
    return true;
  }

  return false;
}

/**
 * Generic event
 */
export function trackEvent(name, params = {}) {
  if (shouldSkipAnalytics(name)) return;
  if (!hasGtag()) return;

  window.gtag("event", name, params);
}

/**
 * SPA page view
 */
export function trackPageView(path, title) {
  if (shouldSkipAnalytics()) return;
  if (!hasGtag()) return;

  window.gtag("event", "page_view", {
    page_location: window.location.href,
    page_path: path,
    page_title: title,
  });
}

/**
 * Landing CTA
 */
export function trackLandingCta({ actType, region, placement }) {
  trackEvent("landing_cta_click", {
    act_type: actType,
    region,
    placement,
  });
}

/* ------------------------------------------------------
 * 🔥 Etusivun eventit
 * ----------------------------------------------------*/

/**
 * Kun käyttäjä valitsee päivän
 */
export function trackDateSelected(date) {
  trackEvent("etusivu_pvm_valittu", {
    pvm: date,
    sivu: "etusivu",
  });
}

/**
 * Kun käyttäjä valitsee esiintyjätyypin
 */
export function trackArtistTypeSelected(typeName) {
  trackEvent("suodatin_tyyppi_valittu", {
    tyyppi: typeName || "Kaikki",
    sivu: "etusivu",
  });
}

/**
 * Kun käyttäjä valitsee maakunnan
 */
export function trackRegionSelected(regionName) {
  trackEvent("suodatin_maakunta_valittu", {
    maakunta: regionName,
    sivu: "etusivu",
  });
}

/**
 * Kun käyttäjä avaa esiintyjän profiilin kortista
 */
export function trackProfileOpened({ id, name, slug, date }) {
  trackEvent("profiili_avattu_listasta", {
    id,
    nimi: name,
    slug,
    pvm: date,
    from_page: "etusivu",
  });
}

/**
 * Kun pikahakumodali avataan
 */
export function trackBookingModalOpened() {
  trackEvent("pikahaku_avattu", {
    from_page: "etusivu",
  });
}

/* ------------------------------------------------------
 * 🔥 Profiilisivun eventit
 * ----------------------------------------------------*/

/**
 * Kun käyttäjä avaa bandiprofiilin.
 */
export function trackProfileView({ id, name, slug }) {
  trackEvent("profiili_avattu", {
    id,
    nimi: name,
    slug,
    sivu: "profile",
  });
}

/**
 * Kun käyttäjä klikkaa somelinkkiä.
 */
export function trackSocialClick({ platform, bandId, bandName }) {
  trackEvent("some_klikattu", {
    platform,
    band_id: bandId,
    band_name: bandName,
    sivu: "profile",
  });
}

/**
 * Kun galleriakuva avataan.
 */
export function trackGalleryOpen({ bandId, bandName, index }) {
  trackEvent("galleria_avattu", {
    band_id: bandId,
    band_name: bandName,
    image_index: index,
    sivu: "profile",
  });
}

/**
 * Kun profiilivideota katsotaan.
 */
export function trackVideoPlay({ bandId, bandName, url }) {
  trackEvent("video_kaynnistetty", {
    band_id: bandId,
    band_name: bandName,
    video_url: url,
    sivu: "profile",
  });
}

/**
 * Kun profiilin kalenterista valitaan päivä.
 */
export function trackProfileCalendarSelect({ bandId, bandName, date }) {
  trackEvent("kalenteri_pvm_valittu", {
    band_id: bandId,
    band_name: bandName,
    date,
    sivu: "profile",
  });
}

/**
 * Kun käyttäjä klikkaa "pyydä tarjous".
 */
export function trackOfferStart({ bandId, bandName }) {
  trackEvent("tarjous_aloitettu", {
    band_id: bandId,
    band_name: bandName,
    sivu: "profile",
  });
}

/**
 * Kun käyttäjä valitsee setin.
 */
export function trackSetSelected({ bandId, bandName, setLabel }) {
  trackEvent("tarjous_setti_valittu", {
    band_id: bandId,
    band_name: bandName,
    set: setLabel,
    sivu: "profile",
  });
}

/**
 * Kun käyttäjä painaa "Lähetä tarjous".
 */
export function trackOfferSubmitClick({ bandId, bandName }) {
  trackEvent("tarjous_lahetys_klikattu", {
    band_id: bandId,
    band_name: bandName,
    sivu: "profile",
  });
}

/**
 * Kun tarjouspyyntö onnistuu.
 */
export function trackOfferSubmitted({ bandId, bandName, price }) {
  trackEvent("tarjous_lahetetty", {
    band_id: bandId,
    band_name: bandName,
    price,
    sivu: "profile",
  });
}

/**
 * Palautteen PIN-tarkistus.
 *
 * Huom:
 * Ei lähetetä sähköpostiosoitetta GA4:ään, koska GA4:ään ei pidä lähettää
 * henkilötietoja tai tunnistettavia käyttäjätietoja.
 */
export function trackFeedbackPinCheck({ slug, bandId, email, hasPin }) {
  trackEvent("palaute_pin_tarkistus", {
    slug,
    band_id: bandId,
    has_email: email ? "1" : "0",
    has_pin: hasPin ? "1" : "0",
    sivu: "booking_form",
  });
}

/* ------------------------------------------------------
 * 🔥 Bookkauslomakkeen eventit
 * ----------------------------------------------------*/

/**
 * Kun lomake avataan.
 */
export function trackBookingFormOpen({
  slug,
  kind,
  mode,
  selectedDate,
  bandId,
  bandName,
}) {
  trackEvent("lomake_avattu", {
    slug: slug || "",
    kind: kind || "",
    mode: mode || "",
    event_date: selectedDate || null,

    // Legacy / mahdolliset muut kutsujat
    band_id: bandId ?? null,
    band_name: bandName || "",

    sivu: "booking_form",
  });
}

/**
 * Kun käyttäjä vaihtaa lomakkeen tilaa.
 */
export function trackBookingFormModeChange({ slug, kind, mode }) {
  trackEvent("lomake_tila_vaihdettu", {
    slug: slug || "",
    kind: kind || "",
    mode,
    sivu: "booking_form",
  });
}

/**
 * Kun käyttäjä muuttaa kenttää.
 *
 * Huom:
 * Ei lähetetä kentän varsinaista arvoa GA4:ään.
 * Lähetetään vain tieto siitä, mihin kenttään syötettiin jotain.
 */
export function trackBookingFieldInput({ field, value }) {
  trackEvent("lomake_kentta_syote", {
    field,
    has_value: value ? "1" : "0",
    sivu: "booking_form",
  });
}

/**
 * Kun käyttäjä valitsee asiakastyypin.
 */
export function trackBookingCustomerTypeSelected({
  slug,
  kind,
  customerType,
}) {
  trackEvent("lomake_asiakastyyppi_valittu", {
    slug: slug || "",
    kind: kind || "",
    customer_type: customerType || "",
    sivu: "booking_form",
  });
}

/**
 * Kun tapahtumapaikka on oikeasti valittu / ratkaistu.
 *
 * Huom:
 * Varsinaista osoitetta EI lähetetä GA4:ään.
 */
export function trackBookingLocationSelected({
  slug,
  kind,
  region,
  locationType,
}) {
  trackEvent("lomake_tapahtumapaikka_valittu", {
    slug: slug || "",
    kind: kind || "",
    region: region || "",
    location_type: locationType || "",
    sivu: "booking_form",
  });
}

/**
 * Kun käyttäjä valitsee esiintymisen / palvelun keston.
 */
export function trackBookingDurationSelected({
  slug,
  kind,
  duration,
}) {
  trackEvent("lomake_kesto_valittu", {
    slug: slug || "",
    kind: kind || "",
    duration: duration || "",
    sivu: "booking_form",
  });
}

/**
 * Kun käyttäjä aloittaa yhteystietojen täyttämisen.
 *
 * Tämä lähetetään Bookkauslomake-komponentista vain kerran
 * per kohde + lomaketila + tapahtumapäivä.
 *
 * Henkilötietoja EI lähetetä GA4:ään.
 */
export function trackBookingContactStarted({
  slug,
  kind,
  mode,
  event_date,
}) {
  trackEvent("lomake_yhteystiedot_aloitettu", {
    slug: slug || "",
    kind: kind || "",
    mode: mode || "",
    event_date: event_date || null,
    sivu: "booking_form",
  });
}

/**
 * Kun käyttäjä painaa Lähetä.
 */
export function trackBookingSubmit({
  slug,
  kind,
  mode,
  event_date,
  bandId,
  bandName,
}) {
  trackEvent("lomake_lahetys_klikattu", {
    slug: slug || "",
    kind: kind || "",
    mode: mode || "",
    event_date: event_date || null,

    // Legacy
    band_id: bandId ?? null,
    band_name: bandName || "",

    sivu: "booking_form",
  });
}

/**
 * Kun API palauttaa OK.
 */
export function trackBookingSubmitSuccess({
  slug,
  kind,
  mode,
  event_date,
  price,
  bandId,
  bandName,
}) {
  trackEvent("lomake_lahetys_ok", {
    slug: slug || "",
    kind: kind || "",
    mode: mode || "",
    event_date: event_date || null,
    price: price ?? null,

    // Legacy
    band_id: bandId ?? null,
    band_name: bandName || "",

    sivu: "booking_form",
  });
}